import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  FileText,
  CheckCircle,
  XCircle,
  Activity,
  Users,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  GET_CONTRACT_TYPE_STATISTICS,
  CREATE_CONTRACT_TYPE_MUTATION,
  UPDATE_CONTRACT_TYPE_MUTATION,
  DELETE_CONTRACT_TYPE_MUTATION,
} from '../../graphql/queries';

interface ContractType {
  id: string;
  code: string;
  displayName: string;
  description: string;
  hasOvertimePay: boolean;
  isActive: boolean;
  sortOrder: number;
  usageCount?: number;
}

interface ContractTypeFormData {
  code: string;
  displayName: string;
  description: string;
  hasOvertimePay: boolean;
  isActive: boolean;
  sortOrder: number;
}

const EMPTY_FORM: ContractTypeFormData = {
  code: '',
  displayName: '',
  description: '',
  hasOvertimePay: true,
  isActive: true,
  sortOrder: 0,
};

// System types that shouldn't have their code edited
const SYSTEM_CODES = ['EMPLOYMENT_CONTRACT', 'CIVIL_CONTRACT'];

export const ContractTypeManagement: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(GET_CONTRACT_TYPE_STATISTICS, {
    fetchPolicy: 'network-only',
  });

  const [createContractType] = useMutation(CREATE_CONTRACT_TYPE_MUTATION);
  const [updateContractType] = useMutation(UPDATE_CONTRACT_TYPE_MUTATION);
  const [deleteContractType] = useMutation(DELETE_CONTRACT_TYPE_MUTATION);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<ContractType | null>(null);
  const [formData, setFormData] = useState<ContractTypeFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingType, setDeletingType] = useState<ContractType | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openCreateDialog = () => {
    setEditingType(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (type: ContractType) => {
    setEditingType(type);
    setFormData({
      code: type.code,
      displayName: type.displayName,
      description: type.description || '',
      hasOvertimePay: type.hasOvertimePay,
      isActive: type.isActive,
      sortOrder: type.sortOrder || 0,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (type: ContractType) => {
    setDeletingType(type);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code.trim() || !formData.displayName.trim()) {
      toast.error('Kod i nazwa wyświetlana są wymagane');
      return;
    }

    setSaving(true);
    try {
      if (editingType) {
        // Update
        await updateContractType({
          variables: {
            id: editingType.id,
            code: formData.code.toUpperCase(),
            displayName: formData.displayName,
            description: formData.description,
            hasOvertimePay: formData.hasOvertimePay,
            isActive: formData.isActive,
            sortOrder: formData.sortOrder,
          },
        });
        toast.success(`Rodzaj umowy "${formData.displayName}" został zaktualizowany`);
      } else {
        // Create
        await createContractType({
          variables: {
            code: formData.code.toUpperCase(),
            displayName: formData.displayName,
            description: formData.description,
            hasOvertimePay: formData.hasOvertimePay,
            isActive: formData.isActive,
            sortOrder: formData.sortOrder,
          },
        });
        toast.success(`Rodzaj umowy "${formData.displayName}" został utworzony`);
      }
      setDialogOpen(false);
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Wystąpił błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    setDeleting(true);
    try {
      await deleteContractType({ variables: { id: deletingType.id } });
      toast.success(`Rodzaj umowy "${deletingType.displayName}" został usunięty`);
      setDeleteDialogOpen(false);
      setDeletingType(null);
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Wystąpił błąd podczas usuwania');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (type: ContractType) => {
    try {
      await updateContractType({
        variables: {
          id: type.id,
          isActive: !type.isActive,
        },
      });
      toast.success(
        type.isActive
          ? `Rodzaj umowy "${type.displayName}" został dezaktywowany`
          : `Rodzaj umowy "${type.displayName}" został aktywowany`
      );
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Wystąpił błąd');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">Ładowanie rodzajów umów...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-500">Błąd podczas ładowania: {error.message}</div>
      </div>
    );
  }

  const contractTypes: ContractType[] = data?.contractTypeStatistics || [];

  // Calculate statistics
  const totalUsage = contractTypes.reduce((sum: number, type: ContractType) => sum + (type.usageCount || 0), 0);
  const activeTypes = contractTypes.filter((type: ContractType) => type.isActive).length;

  const isSystemCode = (code: string) => SYSTEM_CODES.includes(code);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Zarządzanie rodzajami umów</h2>
          <p className="text-muted-foreground">Konfiguracja rodzajów umów pracowników</p>
        </div>
        <Button onClick={openCreateDialog} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Dodaj rodzaj umowy</span>
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-950/40 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Wszystkie typy</p>
                <p className="text-2xl font-bold text-foreground">{contractTypes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 dark:bg-green-950/40 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Aktywne typy</p>
                <p className="text-2xl font-bold text-foreground">{activeTypes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-950/40 rounded-lg">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pracowników z umową</p>
                <p className="text-2xl font-bold text-foreground">{totalUsage}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contract Types Table */}
      <Card>
        <CardHeader>
          <CardTitle>Rodzaje umów</CardTitle>
          <CardDescription>
            Konfiguracja dostępnych rodzajów umów w systemie
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rodzaj umowy</TableHead>
                <TableHead>Opis</TableHead>
                <TableHead className="text-center">Nadgodziny płatne</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Pracownicy</TableHead>
                <TableHead>Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contractTypes.map((type: ContractType) => (
                <TableRow key={type.id}>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <div className="p-2 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                        <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{type.displayName}</div>
                        <div className="text-sm text-muted-foreground">{type.code}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{type.description}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    {type.hasOvertimePay ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                    ) : (
                      <XCircle className="w-5 h-5 text-muted-foreground mx-auto" />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={type.isActive ? "default" : "secondary"}
                      className={
                        type.isActive
                          ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400 cursor-pointer"
                          : "cursor-pointer"
                      }
                      onClick={() => handleToggleActive(type)}
                    >
                      {type.isActive ? 'Aktywny' : 'Nieaktywny'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline">
                      {type.usageCount || 0}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(type)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                        onClick={() => openDeleteDialog(type)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Edytuj rodzaj umowy' : 'Dodaj nowy rodzaj umowy'}
            </DialogTitle>
            <DialogDescription>
              {editingType
                ? 'Zmień ustawienia wybranego rodzaju umowy.'
                : 'Wypełnij dane nowego rodzaju umowy.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="code">
                Kod <span className="text-red-500">*</span>
              </Label>
              {editingType && isSystemCode(formData.code) ? (
                <Input
                  id="code"
                  value={formData.code}
                  readOnly
                  className="bg-muted font-mono"
                />
              ) : (
                <Input
                  id="code"
                  placeholder="np. B2B_CONTRACT"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toUpperCase() })
                  }
                  className="font-mono"
                  required
                />
              )}
              {editingType && isSystemCode(formData.code) && (
                <p className="text-xs text-muted-foreground">
                  Kod systemowego rodzaju umowy nie może być zmieniony.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">
                Nazwa wyświetlana <span className="text-red-500">*</span>
              </Label>
              <Input
                id="displayName"
                placeholder="np. Umowa B2B"
                value={formData.displayName}
                onChange={(e) =>
                  setFormData({ ...formData, displayName: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Opis</Label>
              <Input
                id="description"
                placeholder="Krótki opis rodzaju umowy..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  id="hasOvertimePay"
                  type="checkbox"
                  checked={formData.hasOvertimePay}
                  onChange={(e) =>
                    setFormData({ ...formData, hasOvertimePay: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="hasOvertimePay" className="cursor-pointer">
                  Nadgodziny płatne
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="isActive" className="cursor-pointer">
                  Aktywny
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortOrder">Kolejność</Label>
              <Input
                id="sortOrder"
                type="number"
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })
                }
                min={0}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Anuluj
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? 'Zapisywanie...'
                : editingType
                  ? 'Zapisz zmiany'
                  : 'Utwórz rodzaj umowy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Potwierdź usunięcie</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć rodzaj umowy "{deletingType?.displayName}"?
              Ta operacja jest nieodwracalna.
            </DialogDescription>
          </DialogHeader>
          {deletingType && (deletingType.usageCount || 0) > 0 && (
            <div className="p-3 text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 rounded-md">
              Ten rodzaj umowy jest przypisany do {deletingType.usageCount} pracowników. Usunięcie może wpłynąć na
              istniejące dane.
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingType(null);
              }}
              disabled={deleting}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
